import { SetStateAction, useEffect, useMemo, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import NumericRangeFilter from '@/components/NumericRangeFilter';
import { ROUTES } from '@/constants/application-urls';
import { FILTER_TYPES, FILTER_VISIBLE_PAGES } from '@/constants/filter';
import { DEFAULT_DEBOUNCE_TIME } from '@/constants/static';
import { CategoryType } from '@/enums/CategoryType';
import { FilterParamKey, NumericRangeFilterParam } from '@/enums/FilterParam';
import { useDebounce } from '@/hooks/useDebounce';
import ProductListingDictionary from '@/types/dictionaries/ProductListing';
import { Category } from '@/types/gearApi/Category';
import {
	EnumValues,
	NumericRangeProductFilterConfiguration,
	ProductFilter,
	ReferencedProduct,
} from '@/types/gearApi/Product';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import SearchableSelect from '@/components/SearchableSelect';
import Box from '@mui/material/Box';
import FilterLabelsDictionary from '@/types/dictionaries/FilterLabels';
import {
	ActiveNumericRangeFilter,
	EnumsFilterInput,
	NumberRangeInput,
} from '@/features/FeatureRefinedProductList/actions';
import FilterSelect from '@/components/FilterSelect';
import { specificCategoriesSection } from '@/constants/specific-categories';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

export interface ProductsRefinementProps {
	brandCode: string;
	categories: Category[];
	industries: Category[];
	filterLabels: ProductListingDictionary['filters'];
	categoryKey?: string;
	filterConfig?: ProductFilter[];
	onSelect: (
		selectedCategories: string[],
		selectedIndustries: string[],
		numericRanges: NumberRangeInput,
		selectedProductReference?: string | null,
		selectedEnums?: EnumsFilterInput,
	) => void;
	filterLabelsDict: FilterLabelsDictionary;
}

export default function ProductsRefinement({
	brandCode,
	categories,
	industries,
	filterLabels,
	categoryKey,
	filterConfig = [],
	onSelect,
	filterLabelsDict,
}: Readonly<ProductsRefinementProps>) {
	const searchParams = useSearchParams();

	const [selectedCategories, setSelectedCategories] = useState<string[]>(
		searchParams.get(CategoryType.CATEGORIES)?.split(',') ?? [],
	);
	const [selectedIndustries, setSelectedIndustries] = useState<string[]>(
		searchParams.get(CategoryType.INDUSTRIES)?.split(',') ?? [],
	);
	const [selectedProductReference, setSelectedProductReference] = useState<string | undefined>(
		searchParams.get(FilterParamKey.FitFor) ?? undefined,
	);

	const pathname = usePathname();
	const lastPathSegment = pathname.split('/').filter(Boolean).pop();
	const isSearchPage = lastPathSegment === ROUTES.PRODUCTS.replace('/', '');

	const filtersToRender = useMemo(() => {
		return filterConfig.reduce(
			(acc, filter) => {
				const { $type, visibleOn } = filter.configuration;

				const isVisibleOnAllPages = visibleOn.includes(FILTER_VISIBLE_PAGES.allPages);
				const isVisibleOnSearchPage = isSearchPage && visibleOn.includes(FILTER_VISIBLE_PAGES.searchPage);
				const isVisibleOnCategoryPage = !isSearchPage && visibleOn.includes(categoryKey ?? '');

				if (isVisibleOnAllPages || isVisibleOnSearchPage || isVisibleOnCategoryPage) {
					if ($type === FILTER_TYPES.numericRangeFilter) acc.numeric.push(filter);
					if ($type === FILTER_TYPES.productReferenceFilter) acc.reference.push(filter);
					if ($type === FILTER_TYPES.enumFilter) acc.enum.push(filter);

					if ($type === FILTER_TYPES.industryCategoryFilter) acc.industryCategory = filter;
					if ($type === FILTER_TYPES.productTypeCategoryFilter) acc.productTypeCategory = filter;
				}
				return acc;
			},
			{ numeric: [], reference: [], enum: [], industryCategory: null, productTypeCategory: null } as {
				numeric: ProductFilter[];
				reference: ProductFilter[];
				enum: ProductFilter[];
				industryCategory: ProductFilter | null;
				productTypeCategory: ProductFilter | null;
			},
		);
	}, [filterConfig, categoryKey, isSearchPage]);

	const getInitialRangeValue = () => {
		const result: ActiveNumericRangeFilter = {};

		for (const filter of filtersToRender.numeric) {
			if (filter.configuration.$type === FILTER_TYPES.numericRangeFilter) {
				const { attribute, minValue, maxValue } = filter.configuration as NumericRangeProductFilterConfiguration;

				const fromParam = searchParams.get(`${attribute}${NumericRangeFilterParam.From}`);
				const toParam = searchParams.get(`${attribute}${NumericRangeFilterParam.To}`);

				let from = fromParam && !isNaN(Number(fromParam)) ? Number(fromParam) : minValue;
				let to = toParam && !isNaN(Number(toParam)) ? Number(toParam) : maxValue;

				// Clamp the values to within the allowed min/max
				from = Math.max(minValue, Math.min(from, maxValue));
				to = Math.max(minValue, Math.min(to, maxValue));

				result[attribute] = [from, to];
			}
		}

		return result;
	};

	const getInitialEnumValues = () => {
		const initialValues: EnumsFilterInput = {};

		for (const filter of filtersToRender.enum) {
			if (filter.configuration.$type === FILTER_TYPES.enumFilter) {
				const attribute = filter.configuration.attribute;
				const param = searchParams.get(attribute);

				if (param) {
					initialValues[attribute] = param.split(',');
				} else {
					initialValues[attribute] = [];
				}
			}
		}

		return initialValues;
	};

	const [enumValues, setEnumValues] = useState(getInitialEnumValues);
	const [rangeValues, setRangeValues] = useState(getInitialRangeValue);
	const debouncedRangeValues = useDebounce(rangeValues, DEFAULT_DEBOUNCE_TIME);

	const getActiveNumericRangesFrom = (
		rangeValues: ActiveNumericRangeFilter,
		numericRangeFilters: ProductFilter[],
	): Record<string, number | undefined> => {
		return Object.entries(rangeValues)
			.filter(([attribute]) => {
				const config = numericRangeFilters.find((f) => f.configuration.attribute === attribute)?.configuration as
					| NumericRangeProductFilterConfiguration
					| undefined;
				if (!config || config.$type !== FILTER_TYPES.numericRangeFilter) return false;

				// Check if the range is active
				const [from, to] = rangeValues[attribute] ?? [];
				return from !== config.minValue || to !== config.maxValue;
			})
			.reduce(
				(acc, [attribute, [from, to]]) => {
					acc[`${attribute}${NumericRangeFilterParam.From}`] = from;
					acc[`${attribute}${NumericRangeFilterParam.To}`] = to;
					return acc;
				},
				{} as Record<string, number | undefined>,
			);
	};

	useEffect(() => {
		const activeRanges = getActiveNumericRangesFrom(debouncedRangeValues, filtersToRender.numeric);

		// Flatten all enum values into one object
		const activeEnums: EnumsFilterInput = {};
		Object.entries(enumValues).forEach(([attribute, values]) => {
			activeEnums[attribute] = values && values.length > 0 ? values : null;
		});

		onSelect(selectedCategories, selectedIndustries, activeRanges, selectedProductReference, activeEnums);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedRangeValues, selectedCategories, selectedIndustries, selectedProductReference, enumValues]);

	const handleCategoryChange = (event: SelectChangeEvent<typeof selectedCategories>) => {
		const {
			target: { value },
		} = event;
		const newSelectedCategories = typeof value === 'string' ? value.split(',') : value;
		setSelectedCategories(newSelectedCategories);
	};

	const handleIndustryChange = (event: SelectChangeEvent<typeof selectedIndustries>) => {
		const {
			target: { value },
		} = event;
		const newSelectedIndustries = typeof value === 'string' ? value.split(',') : value;
		setSelectedIndustries(newSelectedIndustries);
	};

	const handleRangeChange = (attribute: string, range: [number | undefined, number | undefined]) => {
		setRangeValues((prev) => ({
			...prev,
			[attribute]: range,
		}));
	};

	const handleEnumChange = (attribute: string, values: string[] | null) => {
		setEnumValues((prev) => ({
			...prev,
			[attribute]: values ?? [],
		}));
	};

	const handleClearFilter = (type: CategoryType) => {
		const activeNumericRanges = Object.entries(rangeValues).reduce(
			(acc, [attribute, [from, to]]) => {
				acc[`${attribute}${NumericRangeFilterParam.From}`] = from;
				acc[`${attribute}${NumericRangeFilterParam.To}`] = to;
				return acc;
			},
			{} as Record<string, number | undefined>,
		);

		// Flatten all enum values into one object
		const activeEnums: EnumsFilterInput = {};
		Object.entries(enumValues).forEach(([attribute, values]) => {
			activeEnums[attribute] = values && values.length > 0 ? values : null;
		});

		if (type === CategoryType.CATEGORIES) {
			const newSelectedCategories: SetStateAction<string[]> = [];
			setSelectedCategories(newSelectedCategories);
			onSelect(newSelectedCategories, selectedIndustries, activeNumericRanges, selectedProductReference, activeEnums);
		} else if (type === CategoryType.INDUSTRIES) {
			const newSelectedIndustries: SetStateAction<string[]> = [];
			setSelectedIndustries(newSelectedIndustries);
			onSelect(selectedCategories, newSelectedIndustries, activeNumericRanges, selectedProductReference, activeEnums);
		}
	};

	const getSelectedCategoryNames = (selected: string[], propCategories: Category[]): string[] => {
		return selected.map((id) => propCategories.find((cat) => cat.id === id)?.name ?? '');
	};

	// styling for items inside of select
	const ITEM_HEIGHT = 48;
	const ITEM_PADDING_TOP = 8;
	const MenuProps = {
		PaperProps: {
			style: {
				maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
				width: 250,
			},
		},
	};

	const isSpecificCategory = (specificCategoriesSection[brandCode] ?? []).includes(categoryKey ?? '');

	const hasFiltersToRender =
		filtersToRender.numeric.length > 0 ||
		filtersToRender.reference.length > 0 ||
		filtersToRender.enum.length > 0 ||
		(filtersToRender.industryCategory !== null && industries.length > 0) ||
		(filtersToRender.productTypeCategory !== null && categories.length > 0);

	return (
		<>
			{hasFiltersToRender && (
				<Typography variant="h6" sx={{ pb: 2, pt: 3 }}>
					{filterLabels.title}
				</Typography>
			)}
			<Box
				display="grid"
				gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
				gap={2}
				sx={(theme) => ({
					'& > *:only-child': {
						maxWidth: { sm: `calc(50% - ${theme.spacing(1)})` },
					},
					position: 'relative',
					pb: 4,
				})}
			>
				{filtersToRender.productTypeCategory && categories.length > 0 && (
					<FormControl fullWidth>
						<InputLabel
							id="selected-categories-label"
							sx={{
								'&.Mui-focused': {
									color: 'grey.dark',
								},
							}}
						>
							{isSpecificCategory ? filterLabels.series : filterLabels.categories}
						</InputLabel>
						<Select
							labelId="selected-categories-label"
							id="selected-categories"
							multiple
							value={selectedCategories}
							onChange={handleCategoryChange}
							input={<OutlinedInput label={isSpecificCategory ? filterLabels.series : filterLabels.categories} />}
							renderValue={() => getSelectedCategoryNames(selectedCategories, categories).join(', ')}
							MenuProps={MenuProps}
							sx={{
								'& .MuiSelect-iconOutlined': {
									display: selectedCategories.length > 0 ? 'none' : '',
									mr: 1,
									color: 'primary.main',
								},
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'grey.dark',
								},
								backgroundColor: 'white.main',
							}}
							IconComponent={ExpandMoreIcon}
							data-testid="category-filter-select"
							endAdornment={
								selectedCategories.length > 0 && (
									<IconButton
										onClick={() => {
											handleClearFilter(CategoryType.CATEGORIES);
										}}
									>
										<HighlightOffIcon />
									</IconButton>
								)
							}
						>
							{categories.map((category) => (
								<MenuItem key={category.id} value={category.id}>
									<Checkbox checked={selectedCategories.some((cat) => cat === category.id)} />
									<ListItemText primary={category.name} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				{filtersToRender.industryCategory && industries.length > 0 && (
					<FormControl fullWidth>
						<InputLabel
							id="selected-industries-label"
							sx={{
								'&.Mui-focused': {
									color: 'grey.dark',
								},
							}}
						>
							{filterLabels.industries}
						</InputLabel>
						<Select
							labelId="selected-industries-label"
							id="selected-industries"
							multiple
							value={selectedIndustries}
							onChange={handleIndustryChange}
							input={<OutlinedInput label={filterLabels.industries} />}
							renderValue={() => getSelectedCategoryNames(selectedIndustries, industries).join(', ')}
							MenuProps={MenuProps}
							sx={{
								'& .MuiSelect-iconOutlined': {
									display: selectedIndustries.length > 0 ? 'none' : '',
									mr: 1,
									color: 'primary.main',
								},
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'grey.dark',
								},
								backgroundColor: 'white.main',
							}}
							IconComponent={ExpandMoreIcon}
							data-testid="industry-filter-select"
							endAdornment={
								selectedIndustries.length > 0 && (
									<IconButton
										onClick={() => {
											handleClearFilter(CategoryType.INDUSTRIES);
										}}
									>
										<HighlightOffIcon />
									</IconButton>
								)
							}
						>
							{industries.map((industry) => (
								<MenuItem key={industry.id} value={industry.id}>
									<Checkbox checked={selectedIndustries.some((ind) => ind === industry.id)} />
									<ListItemText primary={industry.name} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				{/* Render fit for filter dynamically */}
				{filtersToRender.reference.map((filter) => {
					const config = filter.configuration;
					const data = filter.data;

					if (!config || config.$type !== FILTER_TYPES.productReferenceFilter) return null;

					const selectOptions =
						data?.products
							?.map((product: ReferencedProduct) => ({
								label: product.name,
								value: product.id,
							}))
							.sort((a, b) => a.label.localeCompare(b.label)) ?? [];

					const selectedValue = selectOptions.find((opt) => opt.value === selectedProductReference)?.label;

					return (
						<SearchableSelect
							key={filter.key}
							labelText={filterLabels.fitFor}
							value={selectedValue}
							options={selectOptions.map((o) => o.label)}
							onChange={(_, newLabel) => {
								const selectedOption = newLabel ? selectOptions.find((opt) => opt.label === newLabel) : undefined;
								const selectedId = selectedOption?.value;
								setSelectedProductReference(selectedId);
							}}
							testId={filter.key}
							clearIcon={<HighlightOffIcon />}
							sx={{
								backgroundColor: 'white.main',
								'& .MuiFormControl-root.MuiTextField-root label.Mui-focused': {
									color: 'grey.dark',
								},
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'grey.dark',
								},
								'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, \
                                & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: 'grey.ultraDark',
								},
							}}
						/>
					);
				})}

				{/* Render numeric range filters dynamically */}
				{filtersToRender.numeric.map((filter) => {
					const config = filter.configuration as NumericRangeProductFilterConfiguration;
					if (!config || config.$type !== FILTER_TYPES.numericRangeFilter) return null;

					const range = rangeValues[config.attribute] ?? [config.minValue, config.maxValue];

					const label = filterLabelsDict[filter.key as keyof typeof filterLabelsDict] || filterLabels.flowRate;

					return (
						<NumericRangeFilter
							key={filter.key}
							fieldLabel={label}
							min={config.minValue}
							max={config.maxValue}
							step={config.stepSize}
							id={filter.key}
							unitOfMeasure={config.uom}
							initialRange={range}
							onChange={(range) => handleRangeChange(config.attribute, range)}
						/>
					);
				})}

				{/* Render enum filters dynamically */}
				{filtersToRender.enum.map((filter) => {
					const config = filter.configuration;
					const data = filter.data;

					if (!config || config.$type !== FILTER_TYPES.enumFilter) return null;

					const attribute = config.attribute;
					const selectOptions =
						data?.enumKeyLabelValues
							?.map((item: EnumValues) => ({
								label: item.value,
								value: item.key,
							}))
							.sort((a, b) => a.label.localeCompare(b.label)) ?? [];

					const label = filterLabelsDict[filter.key as keyof typeof filterLabelsDict] || filterLabels.housingMaterials;

					return (
						<FilterSelect
							key={filter.key}
							label={label}
							multiple={config.multiSelect}
							options={selectOptions}
							value={enumValues[attribute] ?? []}
							onChange={(newValue) => handleEnumChange(attribute, newValue)}
							testId={filter.key}
						/>
					);
				})}
			</Box>
		</>
	);
}
